import { motion } from "framer-motion";
import { Shield, Lock, Eye, Database, Server, UserCheck } from "lucide-react";
import Footer from "@/components/Footer";
import Header from "@/components/Header";

const privacyContent = [
  {
    icon: <Lock />,
    title: "Data Collection",
    content: `We collect information that you provide directly to us, including:
    • Account information (name, email, password)
    • Usage data and prompts
    • Payment information
    • Communication preferences`
  },
  {
    icon: <Database />,
    title: "Data Storage",
    content: `Your data is stored securely on our servers with:
    • End-to-end encryption
    • Regular security audits
    • Automated backup systems
    • Geographic redundancy`
  },
  {
    icon: <Eye />,
    title: "Data Usage",
    content: `We use your information to:
    • Provide and improve our services
    • Personalize your experience
    • Process payments
    • Send important updates
    • Analyze service performance`
  },
  {
    icon: <UserCheck />,
    title: "Your Rights",
    content: `You have the right to:
    • Access your personal data
    • Request data correction
    • Request data deletion
    • Export your data
    • Opt-out of communications`
  },
  {
    icon: <Server />,
    title: "Data Sharing",
    content: `We may share your information with:
    • Service providers
    • Payment processors
    • Legal authorities when required
    • Business partners (with consent)`
  },
  {
    icon: <Shield />,
    title: "Security Measures",
    content: `We protect your data through:
    • SSL/TLS encryption
    • Regular security assessments
    • Access controls
    • Employee training
    • Incident response plans`
  }
];

const Privacy = () => {
  const lastUpdated = "March 15, 2024";

  return (
    <div className="min-h-screen bg-background">
      <Header 
        title="Privacy Policy"
        description="We take your privacy seriously. Learn how we collect, use, and protect your personal information."
      />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Introduction */}
        <div className="max-w-4xl mx-auto mb-8 sm:mb-16">
          <p className="text-muted-foreground leading-relaxed text-base sm:text-lg">
            This Privacy Policy describes how PromptPerfect ("we," "our," or "us") collects, uses, and shares your personal information when you use our services. By using PromptPerfect, you agree to the collection and use of information in accordance with this policy.
          </p>
        </div>

        {/* Privacy Sections */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-12 sm:mb-16">
          {privacyContent.map((section, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-card p-6 sm:p-8 rounded-xl shadow-lg"
            >
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mr-4">
                  {section.icon}
                </div>
                <h2 className="text-xl sm:text-2xl font-semibold text-foreground">{section.title}</h2>
              </div>
              <div className="text-muted-foreground whitespace-pre-line text-sm sm:text-base">
                {section.content}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Additional Information */}
        <div className="max-w-4xl mx-auto space-y-8 sm:space-y-12">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6 text-foreground">Contact Us</h2>
            <p className="text-muted-foreground text-base sm:text-lg mb-8">
              If you have any questions about this Privacy Policy, please contact us at:
              <br className="hidden sm:block" />
              Email: privacy@promptperfect.ai
              <br className="hidden sm:block" />
              Address: 123 AI Street, Tech City, TC 12345
            </p>
          </div>

          <div>
            <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6 text-foreground">Changes to This Policy</h2>
            <p className="text-muted-foreground text-base sm:text-lg">
              We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "last updated" date at the top of this Privacy Policy. You are advised to review this Privacy Policy periodically for any changes.
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Privacy; 